import { useTheme } from '@mui/material';
import { type MembershipPlansPreviewConfig } from '@provision/membership-plans-ui';
import { useMemo } from 'react';
import { type BundledActivationFlowConfig } from 'src/graphql';
import { type PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';
import {
  formValuesToPreviewConfig,
  fromBranding,
} from 'src/pages/Settings/memberships/utils/form-values-to-preview-config';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

const BRANDING_DEBOUNCE_MS = 200;

interface UseMembershipsConfigPreviewParams {
  activationFlowConfig: BundledActivationFlowConfig | null;
  data: PlansTabFormValues | null;
  liveFormValues: PlansTabFormValues | null;
}

export const useMembershipsConfigPreview = ({
  activationFlowConfig,
  data,
  liveFormValues,
}: UseMembershipsConfigPreviewParams) => {
  const theme = useTheme();

  const previewConfig = useMemo((): MembershipPlansPreviewConfig | null => {
    let fromForm: MembershipPlansPreviewConfig | null = null;
    if (liveFormValues) {
      fromForm = formValuesToPreviewConfig(liveFormValues);
    } else if (data) {
      fromForm = formValuesToPreviewConfig(data);
    }

    if (fromForm) {
      const callForPricePhone = activationFlowConfig?.options?.callForPricingPhone;
      return {
        ...fromForm,
        options: {
          ...fromForm.options,
          ...(callForPricePhone ? { callForPricingPhone: callForPricePhone } : {}),
        },
      };
    }
    if (activationFlowConfig) {
      return activationFlowConfig as unknown as MembershipPlansPreviewConfig;
    }
    return null;
  }, [activationFlowConfig, data, liveFormValues]);

  // Only primary and secondary are used by the membership plans preview.
  const previewBranding = useMemo((): { primaryColor: string; secondaryColor: string } => {
    const fallback = {
      primaryColor: theme.palette.primary.main,
      secondaryColor: theme.palette.secondary?.main ?? theme.palette.primary.main,
    };
    const fromForm = liveFormValues?.options?.branding ?? data?.options?.branding;

    if (fromForm) {
      return fromBranding(fromForm) ?? fallback;
    }

    const fromConfig = activationFlowConfig?.options?.branding as
      | { primaryColor?: string; secondaryColor?: string }
      | undefined;

    if (fromConfig) {
      return fromBranding(fromConfig) ?? fallback;
    }

    return fallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    theme.palette.primary.main,
    theme.palette.secondary?.main,
    liveFormValues?.options?.branding?.primaryColor,
    liveFormValues?.options?.branding?.secondaryColor,
    data?.options?.branding?.primaryColor,
    data?.options?.branding?.secondaryColor,
    activationFlowConfig?.options?.branding?.primaryColor,
    activationFlowConfig?.options?.branding?.secondaryColor,
  ]);

  const debouncedPrimary = useDebouncedValue(
    previewBranding.primaryColor,
    BRANDING_DEBOUNCE_MS,
  );
  const debouncedSecondary = useDebouncedValue(
    previewBranding.secondaryColor,
    BRANDING_DEBOUNCE_MS,
  );
  const previewBrandingDebounced = useMemo(
    () => ({ primaryColor: debouncedPrimary, secondaryColor: debouncedSecondary }),
    [debouncedPrimary, debouncedSecondary],
  );

  const previewBackgroundImageUrl =
    liveFormValues?.options?.branding?.backgroundImageUrl?.trim() ||
    data?.options?.branding?.backgroundImageUrl?.trim() ||
    undefined;

  return {
    previewConfig,
    previewBranding: previewBrandingDebounced,
    previewBackgroundImageUrl,
  };
};
